import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, FileText, Calendar, ArrowLeft, Download,
  TrendingUp, DollarSign, FileCheck
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';

const SalesRegisterPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fromDate, setFromDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    invoice_number: '',
    party_name: '',
    gstin: '',
    total: { min: '', max: '' }
  });

  useEffect(() => {
    if (businessId) {
      fetchSalesRegister();
    }
  }, [fromDate, toDate, businessId]);

  const fetchSalesRegister = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.salesRegister({ from_date: fromDate, to_date: toDate });
      setInvoices(response.data?.rows || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const response = await reportsAPI.exportReport({ report_type: 'sales-register', format, from_date: fromDate, to_date: toDate });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Sales_Register_${fromDate}_${toDate}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  // Apply filters
  const filteredInvoices = invoices.filter(inv => {
    const matchesInvoice = !columnFilters.invoice_number ||
      (inv.invoice_no || '').toLowerCase().includes(columnFilters.invoice_number.toLowerCase());
    const matchesParty = !columnFilters.party_name ||
      (inv.party_name || '').toLowerCase().includes(columnFilters.party_name.toLowerCase());
    const matchesGstin = !columnFilters.gstin ||
      (inv.party_gstin || '').toLowerCase().includes(columnFilters.gstin.toLowerCase());
    const total = inv.total || 0;
    const matchesTotal = (!columnFilters.total?.min || total >= parseFloat(columnFilters.total.min)) &&
      (!columnFilters.total?.max || total <= parseFloat(columnFilters.total.max));
    return matchesInvoice && matchesParty && matchesGstin && matchesTotal;
  });

  const totals = filteredInvoices.reduce((acc, inv) => {
    acc.taxable += inv.taxable || 0;
    acc.cgst += inv.cgst || 0;
    acc.sgst += inv.sgst || 0;
    acc.igst += inv.igst || 0;
    acc.total += inv.total || 0;
    return acc;
  }, { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 });

  const dateRangeLabel = `${new Date(fromDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - ${new Date(toDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;

  return (
    <div className="h-full flex flex-col" data-testid="sales-register-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Sales Register (GST)"
        stats={[
          { icon: FileCheck, label: 'Invoices', value: filteredInvoices.length, color: 'slate' },
          { icon: DollarSign, label: 'Taxable', value: formatCompactCurrency(totals.taxable), color: 'blue' },
          { icon: TrendingUp, label: 'Total GST', value: formatCompactCurrency(totals.cgst + totals.sgst), color: 'emerald' },
          { icon: DollarSign, label: 'Total Value', value: formatCompactCurrency(totals.total), color: 'purple' },
        ]}
        secondaryAction={{
          label: dateRangeLabel,
          icon: Calendar,
          onClick: () => {}
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${businessId}/reports`)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors text-[12px] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                <Calendar className="w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none w-[100px]"
                />
                <span className="text-slate-300">→</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none w-[100px]"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => handleExport('excel')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Excel
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50">
                <FileText className="w-3.5 h-3.5" />
                PDF
              </button>
              <button onClick={() => handleExport('json')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                <Download className="w-3.5 h-3.5" />
                JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading sales register...</p>
              </div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No invoices found</p>
              <p className="text-[13px] text-slate-400 mt-1">Sales invoices for selected period will appear here</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Table Header */}
              <div className="flex-shrink-0">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>Date</th>
                      <th className="tl-th tl-th-teal" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">
                          Invoice No
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.invoice_number}
                            onChange={(val) => setColumnFilters({...columnFilters, invoice_number: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '18%' }}>
                        <div className="flex items-center gap-1">
                          Party Name
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.party_name}
                            onChange={(val) => setColumnFilters({...columnFilters, party_name: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '14%' }}>
                        <div className="flex items-center gap-1">
                          GSTIN
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.gstin}
                            onChange={(val) => setColumnFilters({...columnFilters, gstin: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '12%' }}>Taxable (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>CGST (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>SGST (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '14%' }}>
                        <div className="flex items-center justify-end gap-1">
                          Total (₹)
                          <ColumnFilter 
                            type="number" 
                            value={columnFilters.total}
                            onChange={(val) => setColumnFilters({...columnFilters, total: val})}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Table Body */}
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <tbody className="animate-stagger">
                    {filteredInvoices.map((inv, idx) => (
                      <tr
                        key={idx}
                        className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                      >
                        <td className="tl-td tl-cell-regular" style={{ width: '10%' }}>
                          {new Date(inv.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                        </td>
                        <td className="tl-td tl-cell-primary" style={{ width: '12%' }}>{inv.invoice_no}</td>
                        <td className="tl-td tl-cell-regular" style={{ width: '18%' }}>{inv.party_name || '—'}</td>
                        <td className="tl-td" style={{ width: '14%' }}>
                          {inv.party_gstin ? (
                            <span className="text-[11px] font-mono text-slate-600">{inv.party_gstin}</span>
                          ) : (
                            <span className="text-[11px] text-slate-400">—</span>
                          )}
                        </td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '12%' }}>₹{(inv.taxable || 0).toLocaleString()}</td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '10%' }}>₹{(inv.cgst || 0).toFixed(2)}</td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '10%' }}>₹{(inv.sgst || 0).toFixed(2)}</td>
                        <td className="tl-td text-right text-[13px] font-semibold text-slate-800" style={{ width: '14%' }}>₹{(inv.total || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-teal-50 border-t-2 border-teal-200 sticky bottom-0">
                      <td colSpan="4" className="tl-td text-[13px] font-bold text-slate-900">TOTAL ({filteredInvoices.length} invoices)</td>
                      <td className="tl-td text-right text-[13px] font-bold text-slate-900">₹{totals.taxable.toLocaleString()}</td>
                      <td className="tl-td text-right text-[13px] font-bold text-slate-900">₹{totals.cgst.toFixed(2)}</td>
                      <td className="tl-td text-right text-[13px] font-bold text-slate-900">₹{totals.sgst.toFixed(2)}</td>
                      <td className="tl-td text-right text-[14px] font-bold text-teal-700">₹{totals.total.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredInvoices.length}</span> of <span className="font-medium text-slate-700">{invoices.length}</span> invoices
                </p>
                <p>
                  Total GST Collected: <span className="font-medium text-slate-700">₹{(totals.cgst + totals.sgst).toLocaleString()}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesRegisterPage;
